import React, {
  useState,
  useRef,
  useLayoutEffect,
  useCallback,
  useEffect,
} from 'react'
import { createPortal } from 'react-dom'
import { TooltipProps } from './Tooltip.types'

const arrowClasses = (center: boolean) =>
  center ? 'absolute left-1/2 -translate-x-1/2' : 'absolute right-2'

const tooltipBody = (content: React.ReactNode, centerArrow = false) => (
  <>
    <div className="rounded-lg p-2 sm:p-3 text-xs leading-relaxed shadow-lg bg-[var(--tooltip-bg)] border border-[var(--tooltip-border)] text-[var(--tooltip-text)]">
      {content}
    </div>
    {/* Arrow/caret pointing to info icon */}
    <div
      className={`-bottom-[5px] ${arrowClasses(centerArrow)}`}
      style={{
        width: 0,
        height: 0,
        borderLeft: '6px solid transparent',
        borderRight: '6px solid transparent',
        borderTop: '6px solid var(--tooltip-border)',
      }}
    />
    <div
      className={`-bottom-1 ${arrowClasses(centerArrow)}`}
      style={{
        width: 0,
        height: 0,
        borderLeft: '6px solid transparent',
        borderRight: '6px solid transparent',
        borderTop: '6px solid var(--tooltip-bg)',
      }}
    />
  </>
)

const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  className = '',
  usePortal = true,
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [position, setPosition] = useState<{
    top: number
    left: number
  } | null>(null)
  const triggerRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !tooltipRef.current) return
    const triggerRect = triggerRef.current.getBoundingClientRect()
    const tooltipRect = tooltipRef.current.getBoundingClientRect()
    const gap = 8
    const viewportPadding = 8
    // Center tooltip above trigger so the tail aligns with the icon (mobile and desktop)
    const triggerCenterX = triggerRect.left + triggerRect.width / 2
    let top = triggerRect.top - tooltipRect.height - gap
    let left = triggerCenterX - tooltipRect.width / 2
    if (left < viewportPadding) left = viewportPadding
    if (left + tooltipRect.width > window.innerWidth - viewportPadding) {
      left = window.innerWidth - tooltipRect.width - viewportPadding
    }
    if (top < viewportPadding) top = viewportPadding
    setPosition({ top, left })
  }, [])

  useLayoutEffect(() => {
    if (
      !usePortal ||
      !isVisible ||
      !triggerRef.current ||
      !tooltipRef.current
    ) {
      if (!isVisible) setPosition(null)
      return
    }
    updatePosition()
  }, [isVisible, usePortal, updatePosition])

  useEffect(() => {
    if (!usePortal || !isVisible) return
    window.addEventListener('resize', updatePosition)
    return () => window.removeEventListener('resize', updatePosition)
  }, [usePortal, isVisible, updatePosition])

  const trigger = (
    <div
      ref={triggerRef}
      className={`relative inline-flex ${className}`}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
    </div>
  )

  if (!usePortal) {
    return (
      <div
        className={`relative inline-flex ${className}`}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      >
        {children}
        {isVisible && (
          <div className="absolute bottom-full right-0 mb-2 z-50 w-48 sm:w-64 md:w-80">
            {tooltipBody(content, false)}
          </div>
        )}
      </div>
    )
  }

  const tooltipContent = isVisible ? (
    <div
      ref={tooltipRef}
      className="fixed z-[9999] w-48 sm:w-64 md:w-80"
      style={{
        top: position?.top ?? -9999,
        left: position?.left ?? 0,
        visibility: position ? 'visible' : 'hidden',
      }}
    >
      {tooltipBody(content, true)}
    </div>
  ) : null

  return (
    <>
      {trigger}
      {typeof document !== 'undefined' &&
        createPortal(tooltipContent, document.body)}
    </>
  )
}

export default Tooltip
